import * as React from 'react'
import styled, { DefaultTheme, ThemeProvider } from 'styled-components'
import { Box, muiBankTheme } from '@ally/metronome-ui'
import ChartistGraph, { ChartitGraphProps } from 'react-chartist'
import { SummarizedAccountGroup, GroupedAccounts } from '../../../types'
import BalanceChartTitle from './BalanceChartTitle'

interface BalanceChartProps {
  groupedAccounts: GroupedAccounts
  selected: string
  totalBalance: number
  setSelected: (accountGroup: string) => void
  theme: DefaultTheme
  hasStaleData?: boolean
}

interface ChartConfig {
  className: string
  value: number
}

const StyledChart = styled(ChartistGraph)`
  ${({ theme: { colors } }): string => `
    .ct-chart-donut {
      fill: transparent;
      :hover, &:active  {
        cursor: pointer;
      }
      .ct-series {
        .ct-slice-donut {
          transition: stroke-width 0s ease-out 100ms;
          &:hover, &:active {
            stroke-width: 28px !important;
          }
        }
        &.INVW {
          stroke: ${colors.invw};
        }
        &.INVA {
          stroke: ${colors.inva};
        }
        &.INVS {
          stroke: ${colors.inv};
        }
        &.CHECKING {
          stroke: ${colors.dda};
        }
        &.SAVINGS {
          stroke: ${colors.sda};
        }
        &.CD {
          stroke: ${colors.cda};
        }
        &.IRA {
          stroke: ${colors.ira};
        }
      }
    }
  `}
`

const displayBalance = (balance: number, totalBalance: number): number => {
  /* Make each data segment a min of 1% of the total balance */
  const minDisplayBalance = 0.01 * totalBalance
  return balance < minDisplayBalance ? minDisplayBalance : balance
}

const formatChartData = (
  groupedAccounts: SummarizedAccountGroup[],
  totalBalance: number,
): ChartConfig[] => {
  return groupedAccounts
    .filter(account => account.aggBalance > 0)
    .map(groupedAccount => {
      return {
        className: groupedAccount.accountGroup,
        value: displayBalance(groupedAccount.aggBalance, totalBalance),
      }
    })
    .sort((a, b) => {
      return b.value - a.value
    })
}

const getChartOptions = (props: BalanceChartProps): ChartitGraphProps => {
  const { groupedAccounts, totalBalance } = props
  const chartData = formatChartData(
    Object.values(groupedAccounts),
    totalBalance,
  )

  return {
    data: {
      series: chartData,
    },
    options: {
      height: '260px',
      width: '260px',
      donut: true,
      donutWidth: '20%',
      showLabel: false,
      chartPadding: 12,
    },
    type: 'PieChart',
  }
}

const BalanceChart: React.FC<BalanceChartProps> = (
  props,
): React.ReactElement => {
  const { groupedAccounts, selected, setSelected, hasStaleData } = props
  const { data, options, type } = getChartOptions(props)

  return (
    <ThemeProvider theme={muiBankTheme}>
      <Box
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="balance-chart"
      >
        <BalanceChartTitle
          accountGroup={selected}
          groupedAccounts={groupedAccounts}
          setSelected={setSelected}
          hasStaleData={hasStaleData}
        />
        <StyledChart
          aria-hidden
          data={data}
          options={options}
          type={type}
          listener={{
            draw: (d: any): void => {
              // eslint-disable-next-line no-underscore-dangle
              d.element._node.addEventListener('click', () => {
                setSelected(d.series.className)
              })
              // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-empty-function
              d.element._node.addEventListener('touchstart', () => {})
            },
          }}
        />
      </Box>
    </ThemeProvider>
  )
}

export default BalanceChart
